import React, { useMemo } from 'react'
import Box from '@cloudscape-design/components/box'
import { useSelector } from 'react-redux'
import { getStoreId as getStoreIdFromRedux } from '@atredux/selectors/storeSelector'
import { getToolUrl } from '@services/ToolMenuConfigService'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import './StoreHealthView.scss'

/**
 * StoreHealthView — embeds the Store Health View (SHV) app as an iframe filling the tab panel.
 *
 * Resolved through the centralized tool registry (displayName 'Store Health', assetId
 * 'jwo-store-health-app'). The '/storeHealthView' hash is the SHV route inside the store-health app.
 */
const SHV_DISPLAY_NAME = 'Store Health'
const SHV_ASSET_ID = 'jwo-store-health-app'
const SHV_HASH_PATH = '/storeHealthView'

/**
 * Builds the SHV iframe URL: getToolUrl resolves the base asset URL (or the local-dev override)
 * plus the standard platform query params, then any contributed hash is stripped and the SHV
 * route appended.
 */
const buildShvUrl = (): string => {
    const base = getToolUrl(SHV_DISPLAY_NAME, SHV_ASSET_ID)
    if (!base) {
        console.warn('OperationsPage: could not resolve Store Health View URL')
        return ''
    }
    const hashIndex = base.indexOf('#')
    const cleanBase = hashIndex !== -1 ? base.substring(0, hashIndex) : base
    return `${cleanBase}#${SHV_HASH_PATH}`
}

const StoreHealthView: React.FC = () => {
    const storeId = useSelector(getStoreIdFromRedux)
    const [bundle] = useBundle(BUNDLE_NAME)
    // storeId is not passed to buildShvUrl directly — getToolUrl reads it from Redux internally.
    // Including storeId in deps forces recomputation when the user switches stores, so the iframe
    // reloads with the new store's URL params.
    const shvUrl = useMemo(() => buildShvUrl(), [storeId])

    if (!shvUrl) {
        return (
            <Box padding="l" color="text-status-inactive">
                {t(bundle, 'jsma-web-operations-storeHealthUnavailable')}
            </Box>
        )
    }

    return (
        <div className="operations-shv-container" data-atb-id="operations-shv-container">
            <iframe
                className="operations-shv-frame"
                data-atb-id="operations-shv-frame"
                title={t(bundle, 'jsma-web-operations-storeHealthTitle')}
                aria-label={t(bundle, 'jsma-web-operations-storeHealthTitle')}
                src={shvUrl}
            />
        </div>
    )
}

export default StoreHealthView
